'use client';

import type { ClassValue } from 'clsx';

import {
  ArticleCarousel,
  ArticleCarouselHeader,
  ArticleCarouselItem,
  ArticleCarouselNext,
  ArticleCarouselPrevious,
  ArticleCarouselSteps,
  ArticleCarouselTitle,
  ArticleCarouselTrack,
} from '@vs-shop/commerce-ui/articleCarousel';
import { coalesceNonEmpty } from '@vs-shop/domain/string/coalesceNonEmpty';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';
import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../../utils/intl';

import ArticleDetailPreview from '../detail/ArticleDetailPreview';

import { type RelatedArticle } from '../../../../../effect/schema/openapi/backend';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

type Props = {
  className?: ClassValue;
  heading?: string;
  articles: readonly RelatedArticle[];
};

export default function RelatedArticlesCarousel({ className, articles, heading }: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  if (articles.length === 0) {
    return null;
  }

  return (
    <ArticleCarousel className={className}>
      <ArticleCarouselHeader>
        <ArticleCarouselTitle>
          {heading ??
            intl.formatMessage({
              id: 'product.relatedArticles.articles.title',
              defaultMessage: 'Související články',
            })}
        </ArticleCarouselTitle>
        <ArticleCarouselSteps>
          <ArticleCarouselPrevious />
          <ArticleCarouselNext />
        </ArticleCarouselSteps>
      </ArticleCarouselHeader>
      <ArticleCarouselTrack>
        {articles.map((item) => {
          const imagePathAbstract = item.thumbnailImageAbstract ?? null;
          const imagePath =
            imagePathAbstract !== null
              ? resolveImageUrl(imagePathAbstract, ImageFilter.blog_article_thumb)
              : '';

          const author = item.authors[0] ?? null;

          return (
            <ArticleCarouselItem key={item.slug ?? ''}>
              <ArticleDetailPreview
                routerUrl={item.routerUrl}
                imgSrc={imagePath}
                imgAlt={coalesceNonEmpty(item.metaTitle, item.title) ?? ''}
                heading={item.title ?? ''}
                perex={item.description ?? ''}
                publishedAt={item.publishedAt}
                author={author}
              />
            </ArticleCarouselItem>
          );
        })}
      </ArticleCarouselTrack>
    </ArticleCarousel>
  );
}
